import { useState } from 'react';
import { Box, Typography, Checkbox, Card, Divider, Stack, Chip, Modal, ModalDialog, ModalClose } from '@mui/joy';
import { capitalizeFirstLetter } from '@/utils/strings';
import { formatAmount, formatUnit } from '@/features/units/amounts';
import { EmojiDisplay } from '@/components/EmojiDisplay';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { useGetRecipesQuery } from '@/features/recipes/api';
import { useParams } from 'react-router-dom';
import { RecipeCard } from '@/features/recipes/components/RecipeCard';

export function ShoppingListCategory({ category, entries, isEntryChecked, onToggle, crossOffSelected, showRecipeSources = false }) {
  const allUnchecked = entries.every(entry => !isEntryChecked(entry));
  return (    
    <Box>      
      <Stack direction="row" useFlexGap spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        {category.emoji && <Box sx={{ mr: 0.5 }}><EmojiDisplay emoji={category.emoji} size={16} /></Box>}
        <Typography level="title-sm" component="div" color="neutral.700">{capitalizeFirstLetter(category.name)}</Typography>        
        <Divider orientation="horizontal" sx={{ flex: 1, alignSelf: 'center' }} />
      </Stack>      
      {/* overflow hidden: rows paint their own background, and Joy's Card does not clip children to its radius */}      
      <Card        
        sx={{ p: 0, overflow: 'hidden', boxShadow: allUnchecked ? 'var(--joy-shadow-sm)': 'none' }}
      >      
        <Stack>
          {entries.map((entry, index) => {
            const checked = isEntryChecked(entry);
            const followsChecked = checked && index > 0 && isEntryChecked(entries[index - 1]);
            return (
            <Box
              key={entry.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,  
                pr: 1,                       
                backgroundColor: checked ? 'neutral.200' : 'transparent',
                borderTop: index === 0 ? 'none' : '1px solid',
                borderTopColor: followsChecked ? 'var(--joy-palette-neutral-100)' : 'var(--joy-palette-neutral-200)',                                
              }}
            >
              <Checkbox
                checked={checked}
                onChange={() => onToggle(entry)}
                label={                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Typography level="title-list-item"
                      sx={{
                        textDecoration: checked && crossOffSelected ? 'line-through' : 'none',
                        color: checked && crossOffSelected ? 'text.tertiary' : 'text.primary',
                        
                      }}
                    >
                      {entry.ingredient_name}
                    </Typography>
                    {(formatAmount(entry.amount, entry.unit) !== '0' || entry.unit === 'to taste') && (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        {formatAmount(entry.amount, entry.unit)} {formatUnit(entry.unit)}
                      </Typography>
                    )}                    
                  </Box>                                                        
                }
                sx={{ flex: 1, p: 1.5 }}
              />
              {showRecipeSources && entry.source_recipe_ids?.length > 0 && (
                <RecipeSourcesBadge recipeIds={entry.source_recipe_ids} ingredientName={entry.ingredient_name} />
              )}
            </Box>
            );
          })}
        </Stack>
      </Card>
    </Box>
  );
}

function RecipeSourcesBadge({ recipeIds, ingredientName }) {
  const [open, setOpen] = useState(false);
  const { householdId } = useParams();
  const { data: recipesData } = useGetRecipesQuery({ householdId });
  const allRecipes = recipesData?.data || [];
  
  const sourceRecipes = recipeIds
    .map(id => allRecipes.find(r => r.id === id))
    .filter(Boolean);
  
  if (sourceRecipes.length === 0) {
    return null;
  }

  const handleClick = () => {
    setOpen(true);
  };
  
  return (
    <>
      <Chip
        size="sm"
        variant="soft"
        color="primary"
        startDecorator={<RestaurantIcon sx={{ fontSize: 14 }} />}
        onClick={handleClick}
        sx={{ cursor: 'pointer', ml: 'auto', flexShrink: 0 }}
      >
        {sourceRecipes.length}
      </Chip>
      
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ maxWidth: 500, width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: -0.5 }}>{capitalizeFirstLetter(ingredientName)}</Typography>
          <Typography level="body-sm" sx={{ mb: 1, color: 'text.tertiary' }}>
            Added from {sourceRecipes.length} {sourceRecipes.length === 1 ? 'recipe' : 'recipes'}
          </Typography>
          <Stack component="ul" gap={1} sx={{ p: 0, m: 0, listStyle: 'none' }}>
            {sourceRecipes.map(recipe => (
              <RecipeCard key={recipe.id} recipe={recipe} orientation="horizontal" />
            ))}
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}
